import { Slider } from '@/components/ui/slider';
import { ShieldCheck, AlertTriangle, Check, X } from 'lucide-react';
import { COLORS, RISK_OPTIONS } from '@/constants/calculator';

const PURPLE_TEXT = { color: COLORS.PURPLE };
const ORANGE_TEXT = { color: COLORS.ORANGE };
const PURPLE_BG = { backgroundColor: COLORS.PURPLE };
const PURPLE_BORDER = { borderColor: COLORS.PURPLE };
const ORANGE_BORDER = { borderColor: COLORS.ORANGE };

const SUMMARY_BG = { backgroundColor: 'rgba(79, 56, 126, 0.06)', borderColor: COLORS.PURPLE };

const SELF_DELIVERY_POINTS = [
  { label: 'Lower upfront cost', positive: true },
  { label: 'Higher project risk exposure', positive: false },
  { label: 'Greater potential for cost variation', positive: false },
];

const MANAGED_POINTS = [
  { label: 'Higher cost certainty', positive: true },
  { label: 'Reduced coordination burden', positive: true },
  { label: 'Lower risk exposure', positive: true },
  { label: 'More predictable project outcome', positive: true },
];

const PointRow = ({ point }) => (
  <li className="flex items-start gap-2 text-sm text-slate-700">
    {point.positive ? (
      <Check className="h-4 w-4 mt-0.5 shrink-0" style={ORANGE_TEXT} />
    ) : (
      <X className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
    )}
    <span>{point.label}</span>
  </li>
);

export const RiskPosition = ({
  riskIndex,
  setRiskIndex,
  selfDeliveryCostInclusive,
  managedCost,
  riskOption,
  riskAllowance,
  adjustedSelfDelivery,
}) => {
  const handleChange = (value) => setRiskIndex(value[0]);

  return (
    <div className="mt-12 bg-white border border-slate-200 p-8" data-testid="risk-position-section">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="h-6 w-6" style={PURPLE_TEXT} />
        <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 block">
          YOUR RISK POSITION
        </label>
      </div>

      {/* Slider */}
      <div className="mb-8">
        <Slider
          data-testid="risk-position-slider"
          value={[riskIndex]}
          onValueChange={handleChange}
          min={0}
          max={2}
          step={1}
          className="slider-custom"
        />
        <div className="grid grid-cols-3 mt-3 text-xs sm:text-sm font-semibold text-slate-600">
          {RISK_OPTIONS.map((opt, idx) => {
            const align = idx === 0 ? 'text-left' : idx === RISK_OPTIONS.length - 1 ? 'text-right' : 'text-center';
            const active = idx === riskIndex;
            return (
              <button
                type="button"
                key={opt.key}
                onClick={() => setRiskIndex(idx)}
                className={`${align} transition-colors ${active ? '' : 'hover:text-slate-900'}`}
                style={active ? PURPLE_TEXT : {}}
                data-testid={`risk-option-${opt.key}`}
              >
                {opt.shortLabel}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active selection card */}
      <div className="border-l-4 p-6 mb-8" style={SUMMARY_BG} data-testid="risk-active-label">
        <p className="text-base sm:text-lg font-bold text-slate-900 mb-2">
          &ldquo;{riskOption.label}&rdquo;
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          {riskOption.description}
        </p>
      </div>

      {/* Cost adjustment block */}
      <div className="bg-slate-50 border border-slate-200 p-6 mb-8" data-testid="risk-cost-block">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
          RISK-ADJUSTED LABOUR-ONLY COST
        </p>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-slate-700">Labour-Only Cost (cost-inclusive)</span>
            <span className="font-bold text-slate-900" data-testid="risk-base-cost">
              ${selfDeliveryCostInclusive.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-700">
              + Risk Allowance ({riskOption.allowancePct}%)
            </span>
            <span className="font-bold" style={ORANGE_TEXT} data-testid="risk-allowance-amount">
              +${riskAllowance.toFixed(2)}
            </span>
          </div>
          <div className="border-t border-slate-300 pt-3 flex justify-between items-center">
            <span className="text-base font-bold text-slate-900">= Adjusted Labour-Only Cost</span>
            <span className="text-2xl font-black" style={ORANGE_TEXT} data-testid="risk-adjusted-cost">
              ${adjustedSelfDelivery.toFixed(2)}/hr
            </span>
          </div>
          <div className="border-t border-slate-200 pt-3 flex justify-between items-center text-sm">
            <span className="text-slate-500">vs ARLO Managed Installation</span>
            <span className="font-bold" style={PURPLE_TEXT}>
              ${managedCost.toFixed(2)}/hr
            </span>
          </div>
        </div>
      </div>

      {/* Dynamic messaging */}
      <div className="text-white p-6 mb-8" style={PURPLE_BG} data-testid="risk-dynamic-message">
        <p className="text-sm sm:text-base leading-relaxed">
          {riskOption.dynamicMessage}
        </p>
      </div>

      {/* Side-by-side risk exposure comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border-2 p-6" style={ORANGE_BORDER} data-testid="risk-card-self-delivery">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5" style={ORANGE_TEXT} />
            <h3 className="text-base font-black" style={ORANGE_TEXT}>
              Labour-Only Installation
            </h3>
          </div>
          <ul className="space-y-2">
            {SELF_DELIVERY_POINTS.map((p) => (
              <PointRow key={p.label} point={p} />
            ))}
          </ul>
        </div>

        <div className="border-2 p-6" style={PURPLE_BORDER} data-testid="risk-card-managed">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5" style={PURPLE_TEXT} />
            <h3 className="text-base font-black" style={PURPLE_TEXT}>
              ARLO Managed Installation
            </h3>
          </div>
          <ul className="space-y-2">
            {MANAGED_POINTS.map((p) => (
              <PointRow key={p.label} point={p} />
            ))}
          </ul>
        </div>
      </div>

      {/* Explanatory text */}
      <p className="text-sm leading-relaxed text-slate-600">
        Labour-only installation can appear cheaper because many project risks are not fully visible in the initial price. Delays, rework, missing components, site access issues, supervision time and coordination costs can significantly increase total project cost. This adjustment reflects how much of that risk you are willing to carry.
      </p>
    </div>
  );
};
